import { Button, Dialog, DialogActions } from "@mui/material";
import { type FC, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import type { UUID } from "../../../utils/uuid";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { ProgramsState } from "../../moment/hooks/use-programs";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { SurveyAlertWizardStep1 } from "./survey-alert-wizard-step-1";
import { SurveyAlertWizardStep2 } from "./survey-alert-wizard-step-2";
import { SurveyAlertWizardStep3 } from "./survey-alert-wizard-step-3";
import { SurveyAlertWizardStep4 } from "./survey-alert-wizard-step-4";
import { SurveyAlertWizardStep5 } from "./survey-alert-wizard-step-5";
import { SurveyAlertWizardStep6 } from "./survey-alert-wizard-step-6";

type SurveyAlertWizardDialogProps = {
  open: boolean;
  onClose: () => void;
  programs: ProgramsState;
  channels: MomentFormMoment["composeMethods"];
  connectionCategories: ConnectionCategory[];
  restrictedSegments: UUID[];
};

type SurveyAlertWizardFormValues = MomentFormMoment & {
  content_prompt: string;
};

export const SurveyAlertWizardDialog: FC<SurveyAlertWizardDialogProps> = ({
  open,
  onClose,
  programs,
  channels,
  connectionCategories,
  restrictedSegments,
}) => {
  const [step, setStep] = useState(0);
  const [stepComplete, setStepComplete] = useState(false);
  const methods = useForm<SurveyAlertWizardFormValues>({
    defaultValues: {
      content_prompt: "",
      title: "",
      program_id: undefined,
      channel: null,
      immediate: true,
      schedule: null,
      timezone: null,
      trigger_survey_id: null,
      target_manager: false,
      target_connection_type: null,
      target_channel: false,
      only_additional_recipients: false,
      additional_recipients: [],
      teams_channel_message_notify: false,
      notify_channel: false,
      segment: null,
      cc_bcc: { cc: [], bcc: [] },
    },
  });
  const channel = useWatch({ control: methods.control, name: "channel" });

  const goToStep = (nextStep: number): void => {
    setStepComplete(false);
    setStep(nextStep);
  };

  const handleClose = (): void => {
    methods.reset();
    setStep(0);
    setStepComplete(false);
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { p: 1 } } }}
      >
        {step === 0 && (
          <SurveyAlertWizardStep1 programs={programs} onCompleteChange={setStepComplete} />
        )}
        {step === 1 && (
          <SurveyAlertWizardStep2
            channels={channels}
            onBack={() => goToStep(0)}
            onCompleteChange={setStepComplete}
          />
        )}
        {step === 2 && channel && channel !== "twilio" && channel !== "whatsapp" && (
          <SurveyAlertWizardStep3
            channel={channel}
            connectionCategories={connectionCategories}
            onBack={() => goToStep(1)}
            onCompleteChange={setStepComplete}
            restrictedSegments={restrictedSegments}
          />
        )}
        {step === 3 && (
          <SurveyAlertWizardStep4 onBack={() => goToStep(2)} onCompleteChange={setStepComplete} />
        )}
        {step === 4 && (
          <SurveyAlertWizardStep5 onBack={() => goToStep(3)} onCompleteChange={setStepComplete} />
        )}
        {step === 5 && (
          <SurveyAlertWizardStep6
            connectionCategories={connectionCategories}
            onBack={() => goToStep(4)}
          />
        )}
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} data-analytics-id="survey-alert-wizard-cancel">
            Cancel
          </Button>
          {step <= 4 && (
            <Button
              variant="contained"
              disabled={!stepComplete}
              onClick={() => goToStep(step + 1)}
              data-analytics-id="survey-alert-wizard-next"
            >
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
